import React, { useContext, useEffect, useMemo, useState } from "react";
import moment from "moment-timezone";
import { UserContext } from "../../App";
import { db } from "../../index";
import Select from "react-select";
import { NavItem, NavTabs } from "../layout/Nav";
import { Link } from "react-router-dom";
import { verifyRight } from "../../utils/users";
import styled from "styled-components";
import { Bar, Pie } from "react-chartjs-2";
import {
  ArcElement,
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  PieController,
  Title,
  Tooltip as ChartTooltip,
} from "chart.js";

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  PieController,
  Title,
  ChartTooltip,
  Legend
);

// Styled component for the chart container
const ChartContainer = styled.div`
  margin: 20px 0;
  padding: 20px 0;
  border: 1px solid #eee;
  border-radius: 8px;
  background-color: #f9f9f9;
  width: 100%;
`;

const customStyles = {
  control: (provided) => ({
    ...provided,
    minWidth: "200px",
  }),
};

// Styled components for the invoice stats list
const StatsList = styled.div`
  padding: 0;
  margin: 0;
  list-style: none;
`;

const StatsItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 0;
  border-bottom: 1px solid #eee;
`;

const MonthLabel = styled.div`
  font-size: 16px;
  text-transform: capitalize;
`;

const AmountLabel = styled.div`
  font-size: 16px;
  font-weight: bold;
`;

const AverageLabel = styled.div`
  text-align: center;
  font-size: 16px;
  font-weight: 500;
  margin-top: 10px;
  color: #333;
`;

const LegendContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  margin-top: 15px;
`;

const LegendItem = styled.div`
  display: flex;
  align-items: center;
  margin: 5px 10px;
`;

const LegendColor = styled.div`
  width: 12px;
  height: 12px;
  border-radius: 2px;
  margin-right: 5px;
  background-color: ${(props) => props.color};
`;

export default function InvoiceStatsContent() {
  const { userInfos } = useContext(UserContext);
  const [invoiceList, setInvoiceList] = useState([]);
  const [selectedYear, setSelectedYear] = useState(moment().year());
  const [loaded, setLoaded] = useState(false);
  const [showAllClients, setShowAllClients] = useState(false);

  // Generate year options for the dropdown
  const yearOptions = useMemo(() => {
    const currentYear = moment().year();
    const years = [];
    for (let i = currentYear - 5; i <= currentYear; i++) {
      years.push({
        value: i,
        label: i.toString(),
      });
    }
    return years.reverse();
  }, []);

  // Fetch invoice data
  useEffect(() => {
    if (!userInfos || !userInfos.uid || !userInfos.jwt) return;

    setLoaded(false);

    // Calculate start and end timestamps for the selected year
    const startTimestamp = moment().year(selectedYear).startOf("year").unix();
    const endTimestamp = moment().year(selectedYear).endOf("year").unix();

    // Load invoices from Firebase with filtering by paidAt field
    db.ref("users/" + userInfos.uid + "/invoices")
      .orderByChild("paidAt")
      .startAt(startTimestamp)
      .endAt(endTimestamp)
      .once("value", (snapshot) => {
        let data = snapshot.val();
        if (!data) data = [];
        setInvoiceList(
          Object.keys(data).map((k) => {
            return {
              ...data[k],
              id: k,
            };
          })
        );
        setLoaded(true);
      });
  }, [userInfos, selectedYear]);

  // Calculate monthly statistics
  const monthlyStats = useMemo(() => {
    if (!loaded) return [];

    // Initialize stats for all months
    const stats = Array(12)
      .fill()
      .map((_, i) => ({
        month: i + 1,
        monthName: moment().month(i).format("MMMM"),
        count: 0,
        total: 0,
      }));

    // Calculate stats based on paidAt field
    invoiceList.forEach((invoice) => {
      if (!invoice.paidAt) return;

      const invoiceDate = moment(invoice.paidAt * 1000);
      const invoiceMonth = invoiceDate.month(); // 0-based index

      stats[invoiceMonth].count += 1;

      // Calculate price with taxes included using the correct tax rate
      let priceTTC = 0;

      if (invoice.isOpenField) {
        // For open field invoices, use the tva field from openField
        const priceHT = parseFloat(invoice.openField?.price || 0);
        const taxRate = invoice.openField?.tva || 0;
        priceTTC = priceHT * (1 + taxRate / 100);
      } else {
        // For regular invoices with bookings, calculate the total by summing each booking with its tax rate
        priceTTC =
          invoice.bookings?.reduce((total, booking) => {
            const bookingPrice = parseFloat(booking.price || 0);
            const taxRate = booking.tva || 0;
            return total + bookingPrice * (1 + taxRate / 100);
          }, 0) || parseFloat(invoice.price || 0);
      }

      stats[invoiceMonth].total += priceTTC;
    });

    return stats;
  }, [invoiceList, loaded]);

  // Calculate yearly totals
  const yearlyTotals = useMemo(() => {
    if (!monthlyStats.length) return { count: 0, total: 0 };

    return monthlyStats.reduce(
      (acc, month) => ({
        count: acc.count + month.count,
        total: acc.total + month.total,
      }),
      { count: 0, total: 0 }
    );
  }, [monthlyStats]);

  // Calculate monthly average
  const monthlyAverage = useMemo(() => {
    if (!yearlyTotals.total || !monthlyStats.length) return 0;

    // Get current date
    const currentDate = moment();
    const currentYear = currentDate.year();
    const currentMonth = currentDate.month(); // 0-based index

    // Count months that are either:
    // 1. Have at least one invoice, OR
    // 2. Are fully passed (months before the current month in the selected year)
    const activeMonths = monthlyStats.filter((stat, index) => {
      // Check if month is fully passed
      return (
        selectedYear < currentYear ||
        (selectedYear === currentYear && index < currentMonth)
      );
    }).length;

    // If no active months, use 12 to get the average across the whole year
    const divisor = activeMonths > 0 ? activeMonths : 12;

    return yearlyTotals.total / divisor;
  }, [yearlyTotals.total, monthlyStats, selectedYear]);

  // Calculate top clients data
  const clientsData = useMemo(() => {
    if (!loaded || !invoiceList.length)
      return {
        chartData: { labels: [], datasets: [] },
        allClients: [],
      };

    // Group invoices by customer
    const clientTotals = {};

    invoiceList.forEach((invoice) => {
      if (!invoice.paidAt || !invoice.customer) return;

      const clientId = invoice.customer.id;
      const clientName =
        invoice.customer.name ||
        (invoice.customer.firstname + " " + invoice.customer.lastname).trim();

      if (!clientId) return;

      // Calculate price with taxes included
      let priceTTC = 0;

      if (invoice.isOpenField) {
        const priceHT = parseFloat(invoice.openField?.price || 0);
        const taxRate = invoice.openField?.tva || 0;
        priceTTC = priceHT * (1 + taxRate / 100);
      } else {
        priceTTC =
          invoice.bookings?.reduce((total, booking) => {
            const bookingPrice = parseFloat(booking.price || 0);
            const taxRate = booking.tva || 0;
            return total + bookingPrice * (1 + taxRate / 100);
          }, 0) || parseFloat(invoice.price || 0);
      }

      if (!clientTotals[clientId]) {
        clientTotals[clientId] = {
          id: clientId,
          name: clientName,
          count: 0,
          total: 0,
        };
      }

      clientTotals[clientId].count += 1;
      clientTotals[clientId].total += priceTTC;
    });

    // Convert to array and sort by total
    let sortedClients = Object.values(clientTotals).sort(
      (a, b) => b.total - a.total
    );

    // Take top 5 clients for the chart
    const top5Clients = sortedClients.slice(0, 5);

    // Sum the rest into "Autres"
    const otherClients = sortedClients.slice(5);
    const otherTotal = otherClients.reduce(
      (sum, client) => sum + client.total,
      0
    );

    // Prepare data for pie chart
    const labels = top5Clients.map((client) => client.name);
    const data = top5Clients.map((client) => client.total);

    // Add "Autres" category if there are more than 5 clients
    if (otherClients.length > 0) {
      labels.push("Autres");
      data.push(otherTotal);
    }

    // Generate colors for the pie chart
    const backgroundColors = [
      "#6D00E7", // Primary color
      "#8F45F2",
      "#B07FF8",
      "#D1B9FD",
      "#E6DAFF",
      "#AAAAAA", // Grey for "Autres"
    ];

    return {
      chartData: {
        labels,
        datasets: [
          {
            data,
            backgroundColor: backgroundColors,
            borderWidth: 1,
            borderColor: "#FFFFFF",
          },
        ],
      },
      allClients: sortedClients,
    };
  }, [invoiceList, loaded]);

  const handleYearChange = (option) => {
    setSelectedYear(option.value);
  };

  if (!userInfos) return null;

  return (
    <div className="body-container">
      <div className="title-container">
        <NavTabs className="titles">
          {verifyRight(userInfos, "turnover", "read") && (
            <NavItem>
              <Link to="/turnover/">Turnover</Link>
            </NavItem>
          )}
          {verifyRight(userInfos, "invoice", "read") && (
            <NavItem className="active">
              <Link to="/invoice-stats/">Stats factures</Link>
            </NavItem>
          )}
        </NavTabs>
      </div>

      <div className="d-flex justify-content-between align-items-center mb-4">
        <h3>Statistiques des factures payées en {selectedYear}</h3>
        <div style={{ width: "200px" }}>
          <Select
            options={yearOptions}
            styles={customStyles}
            value={yearOptions.find((option) => option.value === selectedYear)}
            onChange={handleYearChange}
            className="react-select-container"
          />
        </div>
      </div>

      {!loaded ? (
        <div>Chargement des données...</div>
      ) : (
        <>
          <div className="mb-4">
            <h4>
              Total TTC des factures payées en {selectedYear}:{" "}
              {yearlyTotals.count} factures,{" "}
              {new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
              }).format(yearlyTotals.total)}
            </h4>
          </div>

          <ChartContainer>
            <h5
              style={{
                textAlign: "center",
                marginBottom: "15px",
                color: "#333",
                fontWeight: 500,
              }}
            >
              Montant TTC des factures par mois (en €)
            </h5>
            <div style={{ padding: "0 20px" }}>
              <Bar
                data={{
                  labels: monthlyStats.map((stat) =>
                    stat.monthName.substring(0, 3)
                  ),
                  datasets: [
                    {
                      label: "Montant TTC (€)",
                      data: monthlyStats.map((stat) => stat.total),
                      backgroundColor: "#6D00E7",
                      borderRadius: 4,
                      hoverBackgroundColor: "#120E35",
                    },
                  ],
                }}
                options={{
                  responsive: true,
                  maintainAspectRatio: false,
                  plugins: {
                    legend: {
                      display: false,
                    },
                    tooltip: {
                      callbacks: {
                        label: function (context) {
                          return new Intl.NumberFormat("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                          }).format(context.raw);
                        },
                      },
                    },
                  },
                  scales: {
                    y: {
                      beginAtZero: true,
                      ticks: {
                        callback: function (value) {
                          return new Intl.NumberFormat("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                            maximumFractionDigits: 0,
                          }).format(value);
                        },
                      },
                    },
                  },
                }}
                height={300}
              />
            </div>
            <AverageLabel>
              Moy. :{" "}
              {new Intl.NumberFormat("fr-FR", {
                style: "currency",
                currency: "EUR",
              }).format(monthlyAverage)}
              /mois
            </AverageLabel>
          </ChartContainer>

          <div className="kt-portlet__body">
            <StatsList>
              {monthlyStats.map((stat) => (
                <StatsItem key={stat.month}>
                  <MonthLabel>
                    {stat.monthName} {selectedYear} - {stat.count} facture
                    {stat.count !== 1 ? "s" : ""}
                  </MonthLabel>
                  <AmountLabel>
                    {new Intl.NumberFormat("fr-FR", {
                      style: "currency",
                      currency: "EUR",
                    }).format(stat.total)}
                  </AmountLabel>
                </StatsItem>
              ))}
            </StatsList>
          </div>

          {/* Top Clients Section */}
          <ChartContainer style={{ marginTop: "40px" }}>
            <h5
              style={{
                textAlign: "center",
                marginBottom: "15px",
                color: "#333",
                fontWeight: 500,
              }}
            >
              Top clients
            </h5>
            <div className="d-flex">
              {/* Left column: Pie chart */}
              <div style={{ width: "50%" }}>
                <div style={{ height: "300px" }}>
                  {clientsData.chartData.labels.length > 0 ? (
                    <Pie
                      data={clientsData.chartData}
                      options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        plugins: {
                          legend: {
                            display: false,
                          },
                          tooltip: {
                            callbacks: {
                              label: function (context) {
                                const label = context.label || "";
                                const value = context.raw || 0;
                                const total =
                                  context.chart.getDatasetMeta(0).total;
                                const percentage = Math.round(
                                  (value / total) * 100
                                );

                                return `${label}: ${new Intl.NumberFormat(
                                  "fr-FR",
                                  {
                                    style: "currency",
                                    currency: "EUR",
                                  }
                                ).format(value)} (${percentage}%)`;
                              },
                            },
                          },
                        },
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        height: "100%",
                      }}
                    >
                      Aucune donnée disponible pour les clients
                    </div>
                  )}
                </div>

                {/* Custom legend */}
                {clientsData.chartData.labels.length > 0 && (
                  <LegendContainer>
                    {clientsData.chartData.labels.map((label, index) => (
                      <LegendItem key={index}>
                        <LegendColor
                          color={
                            clientsData.chartData.datasets[0].backgroundColor[
                              index
                            ]
                          }
                        />
                        <span>{label}</span>
                      </LegendItem>
                    ))}
                  </LegendContainer>
                )}
              </div>

              {/* Right column: Clients table */}
              <div style={{ width: "50%", padding: "0 20px" }}>
                <h6
                  style={{
                    marginBottom: "15px",
                    color: "#333",
                    fontWeight: 500,
                    borderBottom: "1px solid #eee",
                    paddingBottom: "8px",
                  }}
                >
                  Montants TTC par client
                </h6>

                <StatsList>
                  {clientsData.allClients
                    .slice(
                      0,
                      showAllClients ? clientsData.allClients.length : 5
                    )
                    .map((client) => (
                      <StatsItem key={client.id}>
                        <MonthLabel>
                          {client.name} - {client.count} facture
                          {client.count !== 1 ? "s" : ""}
                        </MonthLabel>
                        <AmountLabel>
                          {new Intl.NumberFormat("fr-FR", {
                            style: "currency",
                            currency: "EUR",
                          }).format(client.total)}
                        </AmountLabel>
                      </StatsItem>
                    ))}
                </StatsList>

                {clientsData.allClients.length > 5 && (
                  <div style={{ textAlign: "center", marginTop: "20px" }}>
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={() => setShowAllClients(!showAllClients)}
                    >
                      {showAllClients ? "Voir moins" : "Voir tous"}
                    </button>
                  </div>
                )}
              </div>
            </div>
          </ChartContainer>
        </>
      )}
    </div>
  );
}
